import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  fetchEncounterById,
  validateCollectionBatch,
  collectSamples,
} from "../../../utils/api";
import BioSpecimenWizard from "./BioSpecimenWizard";
import BackButton from "./BackButton";
import { formatDateTimeInAppTimeZone } from "../../../utils/appDateTime";
import { BIOBANK_ROUTES } from "../../../constants/routes";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FOLLOW_UP_CODE_PATTERN = /-H\d+$/i;

const buildDisplayFollowUpCode = (rootSampleId, milestoneNumber) => {
  if (!rootSampleId || milestoneNumber === null || milestoneNumber === undefined) return "";
  const prefix = /-H$/i.test(rootSampleId) ? rootSampleId.slice(0, -2) : rootSampleId;
  return `${prefix}-H${milestoneNumber}`;
};

const getFollowUpDisplayCode = (encounterData) => {
  const encounter = encounterData?.encounter || {};
  const followUp = encounterData?.followUp || encounterData?.mitraFollowUp || {};
  if (encounter.displayFollowUpCode) return encounter.displayFollowUpCode;
  if (encounter.followUpCode) return encounter.followUpCode;
  if (followUp.displayFollowUpCode) return followUp.displayFollowUpCode;
  const builtCode = buildDisplayFollowUpCode(followUp.rootSampleId, followUp.milestoneNumber);
  if (builtCode) return builtCode;
  if (encounter.followUpId && !UUID_PATTERN.test(encounter.followUpId) && FOLLOW_UP_CODE_PATTERN.test(encounter.followUpId)) {
    return encounter.followUpId;
  }
  return "";
};

const normalizeFollowUpEncounterData = (encounterData) => {
  if (!encounterData) return null;
  const encounter = encounterData?.encounter || {};
  const followUp = encounterData?.followUp || encounterData?.mitraFollowUp || {};
  const displayFollowUpCode = getFollowUpDisplayCode(encounterData);

  return {
    ...encounterData,
    encounter: {
      ...encounter,
      followUpId: encounter.followUpId || followUp.followUpId,
      displayFollowUpCode,
      followUpCode: displayFollowUpCode,
      rootSampleId: followUp.rootSampleId || encounter.rootSampleId,
    },
  };
};

const FollowUpCollection = () => {
  const { encounterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [encounterData, setEncounterData] = useState(() => normalizeFollowUpEncounterData(location.state || null));
  const [quality, setQuality] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingEncounter, setFetchingEncounter] = useState(!location.state);
  const returnTo =
    location.state?.returnTo ||
    location.state?.returnUrl ||
    BIOBANK_ROUTES.SAMPLE_COLLECTION;

  // Extract milestone code from display follow-up code (e.g., "0000000-H1" -> "H1")
  const getMilestoneCode = (displayFollowUpCode) => {
    if (!displayFollowUpCode) return "";
    return displayFollowUpCode.split("-").pop() || "";
  };

  // Extract kitBarcode from display follow-up code (e.g., "0000000-H1" -> "0000000")
  const getKitBarcode = (displayFollowUpCode) => {
    if (!displayFollowUpCode) return "";
    return displayFollowUpCode.split("-")[0] || "";
  };

  // Fetch encounter details if not provided via location state
  useEffect(() => {
    if (!encounterData && encounterId) {
      const fetchEncounterDetails = async () => {
        try {
          setFetchingEncounter(true);
          const data = await fetchEncounterById(encounterId);
          if (!data) {
            throw new Error("Encounter details not found");
          }
          setEncounterData(normalizeFollowUpEncounterData(data));
        } catch (err) {
          console.error("Error fetching encounter details:", err);
          setError("Failed to load encounter details");
        } finally {
          setFetchingEncounter(false);
        }
      };
      fetchEncounterDetails();
    }
  }, [encounterId, encounterData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quality) {
      setError("Please select sample quality");
      return;
    }

    const { encounter } = encounterData;
    const followUpCode = encounter.displayFollowUpCode || encounter.followUpCode;
    if (!followUpCode || !FOLLOW_UP_CODE_PATTERN.test(followUpCode)) {
      setError("Follow-up display code is missing. Please reopen this collection from the follow-up action item or sample collection list.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const milestoneCode = getMilestoneCode(followUpCode);
      const kitBarcode = getKitBarcode(followUpCode);
      const batchId = kitBarcode; // For follow-up, batchId is the same as kitBarcode

      console.log("🔬 Starting follow-up sample collection:", {
        encounterId,
        followUpId: encounter.followUpId,
        followUpCode,
        batchId,
        milestoneCode,
        quality,
      });

      // S01: Use new batch validate + collect endpoints instead of legacy endpoint
      const samplesPayload = [
        {
          sampleLetter: "H",
          sampleQuality: quality,
          complete: "Yes",  // Follow-up samples are always complete
          sampleComments: "",
        },
      ];

      console.log("📤 Step 1: Validating collection batch...");
      await validateCollectionBatch(batchId, encounterId);

      console.log("📤 Step 2: Collecting samples...");
      await collectSamples(encounterId, batchId, samplesPayload);

      console.log("🎉 Follow-up collection completed successfully");

      // Navigate back to dashboard
      navigate(returnTo, {
        state: {
          success: true,
          message: "Follow-up sample collected successfully",
          sampleCollected: true,
        },
      });
    } catch (err) {
      console.error("❌ Error completing follow-up collection:", err);
      setError(err.message || "Failed to complete sample collection");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(returnTo);
  };

  if (fetchingEncounter) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!encounterData) {
    return (
      <Box sx={{ px: 4, py: 4 }}>
        <Alert severity="error">Encounter data not found</Alert>
        <Button variant="outlined" onClick={handleCancel} sx={{ mt: 2 }}>
          Back to Sample Collection
        </Button>
      </Box>
    );
  }

  const { encounter, patient } = encounterData;
  const followUpCode = encounter.displayFollowUpCode || encounter.followUpCode;
  const milestoneCode = getMilestoneCode(followUpCode);

  const Body = (
    <Box>
      <Stack spacing={1}>
        <Typography
          textAlign="center"
          color="primary"
          variant="h4"
          gutterBottom
        >
          Follow-Up Sample Collection - {milestoneCode}
        </Typography>

        <Typography gutterBottom>
          Complete sample collection for this follow-up milestone.
        </Typography>

        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography>Patient Name: {patient.firstName} {patient.lastName}</Typography>
        <Typography>Patient Account Number: {patient.patientAccountNumber || "N/A"}</Typography>
        <Typography>Sample ID: {followUpCode || "N/A"}</Typography>
        <Typography>
          Check-In Date/Time:{" "}
          {encounter.checkInDateTime
            ? formatDateTimeInAppTimeZone(encounter.checkInDateTime, encounter.checkInDateTime)
            : "N/A"}
        </Typography>

        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />

        <form onSubmit={handleSubmit}>
          <FormControl fullWidth required sx={{ mb: 3 }}>
            <InputLabel id="quality-label">Sample Quality</InputLabel>
            <Select
              labelId="quality-label"
              id="quality-select"
              value={quality}
              label="Sample Quality"
              onChange={(e) => setQuality(e.target.value)}
              disabled={loading}
            >
              <MenuItem value="">
                <em>Select quality...</em>
              </MenuItem>
              <MenuItem value="Excellent">Excellent</MenuItem>
              <MenuItem value="Good">Good</MenuItem>
              <MenuItem value="Fair">Fair</MenuItem>
            </Select>
          </FormControl>
        </form>
      </Stack>
    </Box>
  );

  const Buttons = (
    <>
      <BackButton onClick={handleCancel} disabled={loading} />
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading || !quality || !followUpCode}
        sx={{ minWidth: 120, ml: 2 }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Processing...
          </>
        ) : (
          "Confirm Collection"
        )}
      </Button>
    </>
  );

  return <BioSpecimenWizard Body={Body} Buttons={Buttons} />;
};

export default FollowUpCollection;
